// Template fields as people and models read them (UI, prompts, reports): references as `$name`, the `$$` escape
// decoded. A blob this build cannot read as a template is shown as stored, whole. Never for write-back.
import { readJourneyTemplates, readSurfaceRevisionTemplates, readTemplatesOr } from './template-envelope.ts'
import { templateIdentity } from './template-fields.ts'

/** A store-read envelope without its grammar marker: display text is not grammar 2. */
export function withoutGrammarMark<T extends object>(envelope: T): T {
  const { grammar: _grammar, ...rest } = envelope as T & { grammar?: unknown }
  return rest as T
}

/** Store-parsed (grammar 2) steps in display form. */
export function displaySteps<T>(steps: T): T {
  return readTemplatesOr(() => templateIdentity(steps), steps)
}

/** A stored journey steps blob (any grammar) in display form. */
export function displayJourneySteps<T>(blob: T): T {
  if (!blob || typeof blob !== 'object') return blob
  return readTemplatesOr(() => {
    const read = withoutGrammarMark(readJourneyTemplates(blob))
    return 'actions' in read ? { ...read, actions: templateIdentity(read.actions) } : read
  }, blob)
}

/** A store-parsed (grammar 2) tool revision with its steps in display form. */
export function displayToolRevision<T extends { steps: unknown }>(revision: T): T {
  return { ...withoutGrammarMark(revision), steps: displaySteps(revision.steps) }
}

/** A stored surface revision (any grammar; the marker sits on the revision) with its step actions in display form. */
export function displaySurfaceRevision<T>(revision: T): T {
  if (!revision || typeof revision !== 'object' || !('steps' in revision) || !revision.steps || typeof revision.steps !== 'object') return revision
  return readTemplatesOr(() => {
    const read = withoutGrammarMark(readSurfaceRevisionTemplates(revision as T & { steps: { actions: unknown } }))
    return { ...read, steps: { ...read.steps, actions: templateIdentity(read.steps.actions) } }
  }, revision)
}
